import API_BASE from '../config';
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, Mail, ArrowRight, Loader2, KeyRound,
  Shield, Eye, EyeOff, CheckCircle2
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// ─── Left panel trust signals ─────────────────────────────────────────────────
const trustSignals = [
  { label: "Monte Carlo Simulations",  value: "10,000+"  },
  { label: "Countries Covered",        value: "50+"      },
  { label: "Regulatory Frameworks",    value: "356+"      },
  { label: "Control Points Assessed",  value: "34"       },
];

const features = [
  "Actuarial-grade cyber risk quantification",
  "Real-time regulatory compliance radar — 356+ frameworks",
  "Credential breach & dark web monitoring",
  "Board-ready 19-slide enterprise intelligence deck",
  "ISO 27001, NIST, NDPA & CBN framework alignment",
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "signup" | "verify" | "forgot" | "forgot_verify">(
    location.state?.initialMode || "login"
  );
  const selectedTier = location.state?.selectedTier || "Starter";
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [newPassword,   setNewPassword]   = useState("");
  const [otp,           setOtp]           = useState("");
  const [showPass,      setShowPass]      = useState(false);
  const [isLoading,     setIsLoading]     = useState(false);
  const [error,         setError]         = useState("");
  const [resetSuccess,  setResetSuccess]  = useState(false);
  const [deviceVerifyMode, setDeviceVerifyMode] = useState(false);
  const [deviceEmail,      setDeviceEmail]      = useState("");
  const [deviceOtp,        setDeviceOtp]        = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return null;
    const checks = {
      length:    pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      number:    /[0-9]/.test(pwd),
    };
    const score = Object.values(checks).filter(Boolean).length;
    if (score === 3) return { label: "Strong",  color: "text-emerald-500", bars: 3 };
    if (score === 2) return { label: "Medium",  color: "text-amber-500",   bars: 2 };
    return             { label: "Weak",    color: "text-red-500",     bars: 1 };
  };

  const strength = mode === "signup" ? getPasswordStrength(password) : null;

  const handleResendOtp = async () => {
    setResendLoading(true); setResendSuccess(false); setError("");
    try {
      await axios.post(`${API_BASE}/api/auth/resend-otp`, { email });
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to resend code. Please try again.");
    } finally { setResendLoading(false); }
  };

  const handleForgotRequest = async () => {
    if (!email) { setError("Please enter your email address"); return; }
    setIsLoading(true); setError("");
    try {
      await axios.post(`${API_BASE}/api/auth/reset-request`, { email });
      setMode("forgot_verify");
    } catch {
      setMode("forgot_verify");
    } finally { setIsLoading(false); }
  };

  const handleForgotVerify = async () => {
    if (!otp || !newPassword) { setError("Please enter the code and new password"); return; }
    if (newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
    setIsLoading(true); setError("");
    try {
      await axios.post(`${API_BASE}/api/auth/reset-confirm`, {
        email, code: otp, new_password: newPassword
      });
      setResetSuccess(true);
      setTimeout(() => { setMode("login"); setResetSuccess(false); setOtp(""); setNewPassword(""); }, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Invalid or expired code. Please try again.");
    } finally { setIsLoading(false); }
  };

    const handleDeviceVerify = async () => {
    if (!deviceOtp || deviceOtp.length !== 6) { setError("Please enter the 6-digit code"); return; }
    setIsLoading(true); setError("");
    try {
      const res = await axios.post(`${API_BASE}/api/auth/verify-device`, {
        email: deviceEmail,
        code: deviceOtp,
      });
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("vs_device_token", res.data.device_token);
      window.location.href = "/dashboard";
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Invalid or expired code. Please try again.");
    } finally { setIsLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    if (mode === "signup") {
      if (password.length < 8) { setError("Password must be at least 8 characters"); setIsLoading(false); return; }
      if (!/[A-Z]/.test(password)) { setError("Password must contain at least one uppercase letter"); setIsLoading(false); return; }
      if (!/[0-9]/.test(password)) { setError("Password must contain at least one number"); setIsLoading(false); return; }
    }
    try {
      if (mode === "login") {
        const deviceToken = localStorage.getItem("vs_device_token") || "";
        const res = await axios.post(`${API_BASE}/api/auth/login`, { email, password, device_token: deviceToken });
        if (res.data.requires_device_verification) {
          setDeviceEmail(email);
          setDeviceVerifyMode(true);
          setIsLoading(false);
          return;
        }
        localStorage.setItem("token", res.data.access_token);
        window.location.href = "/dashboard";
      } else if (mode === "signup") {
        await axios.post(`${API_BASE}/api/auth/signup`, { email, password });
        setMode("verify");
      } else if (mode === "verify") {
        await axios.post(`${API_BASE}/api/auth/verify`, { email, code: otp });
        const res = await axios.post(`${API_BASE}/api/auth/login`, { email, password });
        localStorage.setItem("token", res.data.access_token);
        window.location.href = "/dashboard";
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Authentication error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => { setMode(mode === "login" ? "signup" : "login"); setError(""); };

  return (
    <div className="min-h-screen flex font-['Inter'] bg-white">
      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[52%] bg-[#0f172a] flex-col justify-between p-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: "32px 32px" }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
          w-[500px] h-[500px] bg-red-600 opacity-[0.06] rounded-full blur-[120px] pointer-events-none" />
        <div className="relative z-10">
          <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer group w-fit">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center
              shadow-xl shadow-red-900/40 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-lg">V</span>
            </div>
            <span className="text-white font-black text-xl tracking-tighter uppercase">VicShield</span>
          </div>
        </div>
        <div className="relative z-10 space-y-10">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-4">
              Enterprise Risk Intelligence
            </p>
            <h2 className="text-4xl font-black text-white tracking-tighter leading-tight uppercase">
              Know exactly how much your enterprise
              <span className="text-red-500"> is at risk.</span>
            </h2>
          </div>
          <ul className="space-y-3">
            {features.map((f, i) => (
              <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08, duration: 0.3 }} className="flex items-center gap-3">
                <CheckCircle2 size={15} className="text-red-500 flex-shrink-0" />
                <span className="text-slate-300 text-sm font-medium">{f}</span>
              </motion.li>
            ))}
          </ul>
          <div className="grid grid-cols-2 gap-4">
            {trustSignals.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/8 rounded-2xl p-5">
                <p className="text-2xl font-black text-white tracking-tighter">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
            © 2026 VicShield Global. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── RIGHT PANEL ─────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          <div className="flex lg:hidden items-center gap-3 mb-10 justify-center">
            <div onClick={() => navigate("/")}
              className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center shadow-xl shadow-red-200 cursor-pointer">
              <span className="text-white font-black text-lg">V</span>
            </div>
            <span className="text-slate-900 font-black text-xl tracking-tighter uppercase">VicShield</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={mode} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className="mb-8">
              <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                {mode === "login"  ? "Welcome Back"                    :
                 mode === "signup" ? `Create ${selectedTier} Account`  :
                                     "Verify Your Email"}
              </h1>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1.5">
                {mode === "login"  ? "Sign in to your intelligence portal"  :
                 mode === "signup" ? "Begin your cyber risk audit journey"  :
                                     "Enter the 6-digit code sent to your email"}
              </p>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className={`mb-6 p-4 rounded-2xl flex items-start gap-3 border ${
                  error.toLowerCase().includes("enterprise platform") || error.toLowerCase().includes("company email")
                    ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-100"
                }`}>
                <Shield size={15} className={`flex-shrink-0 mt-0.5 ${
                  error.toLowerCase().includes("enterprise platform") || error.toLowerCase().includes("company email")
                    ? "text-amber-500" : "text-red-500"
                }`} />
                <div>
                  <p className={`text-xs font-bold leading-relaxed ${
                    error.toLowerCase().includes("enterprise platform") || error.toLowerCase().includes("company email")
                      ? "text-amber-700" : "text-red-600"
                  }`}>{error}</p>
                  {(error.toLowerCase().includes("enterprise platform") || error.toLowerCase().includes("company email")) && (
                    <p className="text-[10px] font-bold text-amber-600 mt-1.5 uppercase tracking-widest">
                      Example: name@yourcompany.com
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        {deviceVerifyMode ? (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield size={28} className="text-white" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-2">
                New Device Detected
              </h2>
              <p className="text-sm text-slate-500 font-medium">
                A verification code has been sent to <strong>{deviceEmail}</strong>
              </p>
              <p className="text-xs text-slate-400 mt-1">
                This device will be remembered for 30 days
              </p>
            </div>
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">
                Verification Code
              </label>
              <input
                type="text"
                maxLength={6}
                value={deviceOtp}
                onChange={e => setDeviceOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="w-full px-5 py-4 border-2 border-slate-100 rounded-2xl
                  text-2xl font-black text-slate-900 text-center tracking-[0.5em]
                  focus:outline-none focus:border-red-500 transition-all"
              />
            </div>
            {error && (
              <p className="text-xs font-bold text-red-500 text-center">{error}</p>
            )}
            <button
              onClick={handleDeviceVerify}
              disabled={isLoading || deviceOtp.length !== 6}
              className="w-full bg-red-600 text-white py-4 rounded-2xl font-black
                text-[11px] uppercase tracking-widest hover:bg-slate-900
                transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 size={16} className="animate-spin" /> : <><CheckCircle2 size={16} /> Verify Device</>}
            </button>
            <button
              onClick={() => { setDeviceVerifyMode(false); setDeviceOtp(""); setError(""); }}
              className="w-full text-slate-400 text-xs font-bold uppercase tracking-widest
                hover:text-slate-700 transition-colors"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <>
                        <form onSubmit={handleSubmit} className="space-y-4">
              {mode !== "verify" && (
                <>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Work Email</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        className="w-full pl-11 pr-4 py-4 bg-white border-2 border-slate-100 rounded-2xl
                          text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal
                          focus:outline-none focus:border-red-500 transition-all duration-200" />
                    </div>
                    {mode === "signup" && (
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest pl-1">
                        Company email required — personal emails not accepted
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Password</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type={showPass ? "text" : "password"} required value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                        className="w-full pl-11 pr-12 py-4 bg-white border-2 border-slate-100 rounded-2xl
                          text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal
                          focus:outline-none focus:border-red-500 transition-all duration-200" />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {mode === "signup" && password && strength && (
                      <div className="pt-1">
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3].map(i => (
                            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${
                              i <= strength.bars
                                ? strength.bars === 3 ? "bg-emerald-500" : strength.bars === 2 ? "bg-amber-500" : "bg-red-500"
                                : "bg-slate-100"
                            }`} />
                          ))}
                        </div>
                        <p className={`text-[9px] font-black uppercase tracking-widest ${strength.color}`}>
                          {strength.label} password{strength.bars < 3 && " — add uppercase letters and numbers"}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {mode === "verify" && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    6-Digit Verification Code
                  </label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" required value={otp} onChange={(e) => setOtp(e.target.value)}
                      placeholder="000000" maxLength={6}
                      className="w-full pl-11 pr-4 py-5 bg-white border-2 border-slate-100 rounded-2xl
                        text-center text-2xl font-black tracking-[0.5em] text-slate-900
                        placeholder:text-slate-200 focus:outline-none focus:border-red-500 transition-all duration-200" />
                  </div>
                  <p className="text-[10px] font-bold text-slate-400 text-center pt-1">
                    Check your email inbox for the verification code
                  </p>
                  <div className="text-center pt-1">
                    {resendSuccess ? (
                      <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center justify-center gap-1">
                        <CheckCircle2 size={11} /> New code sent — check your email
                      </p>
                    ) : (
                      <button type="button" onClick={handleResendOtp} disabled={resendLoading}
                        className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:text-slate-900 transition-colors disabled:opacity-50">
                        {resendLoading ? "Sending..." : "Resend Code"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <button type="submit" disabled={isLoading}
                className="w-full py-4 mt-2 bg-red-600 text-white rounded-2xl font-black
                  text-[11px] uppercase tracking-widest hover:bg-slate-900 shadow-lg shadow-red-100
                  transition-all duration-200 flex items-center justify-center gap-2
                  active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group">
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : (
                  <>
                    {mode === "login" ? "Secure Sign In" : mode === "signup" ? "Create Account" : "Verify & Enter"}
                    <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>

              {mode === "login" && (
                <div className="text-center mt-4">
                  <button type="button" onClick={() => { setMode("forgot"); setError(""); }}
                    className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-600 transition-colors">
                    Forgot Password?
                  </button>
                </div>
              )}
            </form>

        {mode === "forgot" && (
            <div className="mt-6">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-1">Reset Password</h3>
              <p className="text-xs text-slate-400 font-medium mb-5">Enter your corporate email and we will send a reset code.</p>
              <div className="relative mb-4">
                <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full pl-11 pr-4 py-4 rounded-2xl border-2 border-slate-100
                    text-sm font-medium text-slate-900 bg-slate-50 focus:outline-none focus:border-red-400 focus:bg-white transition-all" />
              </div>
              {error && <p className="text-xs text-red-500 font-bold mb-3">{error}</p>}
              <button onClick={handleForgotRequest} disabled={isLoading}
                className="w-full py-4 bg-red-600 text-white rounded-2xl font-black text-[11px] uppercase
                  tracking-widest hover:bg-slate-900 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : "Send Reset Code"}
              </button>
              <button onClick={() => { setMode("login"); setError(""); }}
                className="w-full mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 transition-colors text-center">
                Back to Sign In
              </button>
            </div>
          )}

          {mode === "forgot_verify" && (
            <div className="mt-6">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-1">Enter Reset Code</h3>
              <p className="text-xs text-slate-400 font-medium mb-5">Check your email for the 6-digit reset code.</p>
              <input type="text" value={otp} onChange={e => setOtp(e.target.value)}
                placeholder="6-digit code" maxLength={6}
                className="w-full px-4 py-4 rounded-2xl border-2 border-slate-100 text-center text-2xl
                  font-black tracking-[0.5em] text-slate-900 bg-slate-50 focus:outline-none
                  focus:border-red-400 focus:bg-white transition-all mb-4" />
              <div className="relative mb-4">
                <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type={showPass ? "text" : "password"} value={newPassword}
                  onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 8 characters)"
                  className="w-full pl-11 pr-11 py-4 rounded-2xl border-2 border-slate-100 text-sm
                    font-medium text-slate-900 bg-slate-50 focus:outline-none focus:border-red-400 focus:bg-white transition-all" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {error && <p className="text-xs text-red-500 font-bold mb-3">{error}</p>}
              {resetSuccess && (
                <p className="text-xs text-emerald-600 font-bold mb-3 flex items-center gap-2">
                  <CheckCircle2 size={13} /> Password reset successfully — redirecting...
                </p>
              )}
              <button onClick={handleForgotVerify} disabled={isLoading || resetSuccess}
                className="w-full py-4 bg-red-600 text-white rounded-2xl font-black text-[11px] uppercase
                  tracking-widest hover:bg-slate-900 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : "Reset Password"}
              </button>
              <button onClick={() => { setMode("login"); setError(""); setOtp(""); }}
                className="w-full mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 transition-colors text-center">
                Back to Sign In
              </button>
            </div>
          )}

          {!deviceVerifyMode && mode !== "verify" && (
            <div className="mt-8 pt-6 border-t border-slate-100 text-center">
              <p className="text-xs font-bold text-slate-400">
                {mode === "login" ? "Don't have an account?" : "Already have an account?"}
              </p>
              <button onClick={switchMode}
                className="mt-2 text-[11px] font-black uppercase tracking-widest text-red-600 hover:text-slate-900 transition-colors">
                {mode === "login" ? "Create Account" : "Sign In Instead"}
              </button>
            </div>
          )}

          {mode === "verify" && (
            <div className="mt-6 text-center">
              <button onClick={() => setMode("signup")}
                className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 transition-colors">
                ← Back
              </button>
            </div>
          )}

          <p className="mt-8 text-center text-[10px] font-bold text-slate-300 uppercase tracking-widest">
            <button onClick={() => navigate("/")} className="hover:text-slate-500 transition-colors">
              ← Back to vicshield.com
            </button>
          </p>
          </>
        )}
        </div>
      </div>
    </div>
  );
}